// src/pages/AssetDetails.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, Form, Button, Spinner, Badge, Row, Col } from "react-bootstrap";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";

export default function AssetDetails() {
  const { user } = useAuth(); // not strictly required here, but often useful for auth headers etc.
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [sort, setSort] = useState({ key: "assetId", dir: "asc" });

  const formatDate = (val) => {
    if (!val) return "-";
    // Handles both date-only and ISO datetime strings
    const d = new Date(val);
    return isNaN(d.getTime()) ? val : d.toLocaleDateString();
  };

  const safeFetchJson = useCallback(async (url, init) => {
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      ...init,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText} – ${text}`);
    }
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/json")) {
      const text = await res.text();
      return text ? JSON.parse(text) : [];
    }
    return res.json();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const items = await safeFetchJson(`${Base_URL}/asset`);
      const arr = Array.isArray(items) ? items : [];
      setData(arr);
      setFiltered(arr);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [safeFetchJson]);

  useEffect(() => {
    load();
  }, [load]);

  // simple client-side search across id, name, description
  useEffect(() => {
    const term = q.trim().toLowerCase();
    if (!term) {
      setFiltered(data);
      return;
    }
    setFiltered(
      data.filter((a) => {
        const hay = [
          a.assetId,
          a.assetName,
          a.assetDescription,
          a.depreciationMethodId,
          a.depreciationStartBasisId,
          a.assetCapacityUnit,
        ]
          .map((x) => (x ?? "").toString().toLowerCase())
          .join(" | ");
        return hay.includes(term);
      })
    );
  }, [q, data]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    arr.sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];

      // numeric compare if both are numbers
      const na = typeof va === "number";
      const nb = typeof vb === "number";
      if (na && nb) {
        return dir === "asc" ? va - vb : vb - va;
      }

      // date compare if both look like dates
      const da = va ? new Date(va) : null;
      const db = vb ? new Date(vb) : null;
      if (da instanceof Date && !isNaN(da) && db instanceof Date && !isNaN(db)) {
        return dir === "asc" ? da - db : db - da;
      }

      // string fallback
      const sa = (va ?? "").toString().toLowerCase();
      const sb = (vb ?? "").toString().toLowerCase();
      if (sa < sb) return dir === "asc" ? -1 : 1;
      if (sa > sb) return dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort]);

  const setSortKey = (key) => {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
    );
  };

  return (
    <div className="container py-3">
      <Row className="align-items-center mb-3 g-2">
        <Col xs="12" md="6">
          <h4 className="m-0">Asset Details</h4>
        </Col>
        <Col xs="12" md="6" className="d-flex justify-content-md-end gap-2">
          <Form.Control
            placeholder="Search by ID, Name, Description, Method, Basis, Unit…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Button variant="outline-secondary" onClick={load} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
          </Button>
        </Col>
      </Row>

      {err && (
        <div className="alert alert-danger py-2">
          <strong>Error:</strong> {err}
        </div>
      )}

      <div className="table-responsive">
        <Table hover bordered size="sm" className="align-middle">
          <thead className="table-light">
            <tr>
              <Th onClick={() => setSortKey("assetId")} active={sort.key === "assetId"} dir={sort.dir}>
                Asset ID
              </Th>
              <Th onClick={() => setSortKey("assetName")} active={sort.key === "assetName"} dir={sort.dir}>
                Name
              </Th>
              <Th onClick={() => setSortKey("assetDescription")} active={sort.key === "assetDescription"} dir={sort.dir}>
                Description
              </Th>
              <Th>Capacity</Th>
              <Th onClick={() => setSortKey("capitalizationDate")} active={sort.key === "capitalizationDate"} dir={sort.dir}>
                Capitalization
              </Th>
              <Th onClick={() => setSortKey("usefulLifeYears")} active={sort.key === "usefulLifeYears"} dir={sort.dir}>
                Life (yrs)
              </Th>
              <Th onClick={() => setSortKey("depreciationMethodId")} active={sort.key === "depreciationMethodId"} dir={sort.dir}>
                Depr. Method
              </Th>
              <Th onClick={() => setSortKey("depreciationRate")} active={sort.key === "depreciationRate"} dir={sort.dir}>
                Depr. Rate
              </Th>
              <Th onClick={() => setSortKey("depreciationStartBasisId")} active={sort.key === "depreciationStartBasisId"} dir={sort.dir}>
                Start Basis
              </Th>
              <Th onClick={() => setSortKey("warrantyStartDate")} active={sort.key === "warrantyStartDate"} dir={sort.dir}>
                Warranty Start
              </Th>
              <Th onClick={() => setSortKey("warrantyEndDate")} active={sort.key === "warrantyEndDate"} dir={sort.dir}>
                Warranty End
              </Th>
              <Th onClick={() => setSortKey("assetActive")} active={sort.key === "assetActive"} dir={sort.dir}>
                Active
              </Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={12} className="text-center py-4">
                  <Spinner animation="border" />
                </td>
              </tr>
            ) : sorted.length === 0 ? (
              <tr>
                <td colSpan={12} className="text-center py-3">
                  No assets found.
                </td>
              </tr>
            ) : (
              sorted.map((a) => (
                <tr key={a.assetId}>
                  <td className="fw-semibold">{a.assetId}</td>
                  <td>{a.assetName}</td>
                  <td>{a.assetDescription}</td>
                  <td>
                    {a.assetCapacity ?? "-"}{" "}
                    <small className="text-muted">{a.assetCapacityUnit || ""}</small>
                  </td>
                  <td>{formatDate(a.capitalizationDate)}</td>
                  <td>{a.usefulLifeYears ?? "-"}</td>
                  <td>{a.depreciationMethodId}</td>
                  <td>{a.depreciationRate ?? "-"}</td>
                  <td>{a.depreciationStartBasisId}</td>
                  <td>{formatDate(a.warrantyStartDate)}</td>
                  <td>{formatDate(a.warrantyEndDate)}</td>
                  <td>
                    {a.assetActive ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function Th({ children, onClick, active, dir }) {
  return (
    <th
      role="button"
      onClick={onClick}
      className={`user-select-none ${onClick ? "text-nowrap" : ""}`}
      title={onClick ? "Click to sort" : undefined}
    >
      <span className="d-inline-flex align-items-center gap-1">
        {children}
        {onClick && (
          <SortIcon active={active} dir={dir} />
        )}
      </span>
    </th>
  );
}

function SortIcon({ active, dir }) {
  // simple caret indicator
  return (
    <span style={{ opacity: active ? 1 : 0.3 }}>
      {dir === "asc" ? "▲" : "▼"}
    </span>
  );
}
